import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';

dayjs.extend(isToday);

/**
 * Cell renderer for phone number values.
 * Formats the number using `formatPhoneNumber` or returns '-' if empty.
 *
 * @example
 * // In a column config:
 * { cellRenderer: phoneNumberCellRenderer }
 */
export const phoneNumberCellRenderer = (value: string): string => (value ? formatPhoneNumber(value) : '-');

type DateTimeCellRendererOptions =
  | string
  | {
      /** When true, uses smart formatting: "Today h:mm A" for today, "MMM D, h:mm A" for current year, "MMM D, YYYY h:mm A" for other years. */
      smart?: boolean;
      /** Fallback format used when smart is false or not provided. Defaults to 'MMM D, YYYY h:mm A'. */
      format?: string;
    };

/**
 * Formats a date string using smart formatting rules:
 * - Today → "Today h:mm A" (e.g., "Today 1:30 PM")
 * - Current year → "MMM D, h:mm A" (e.g., "Aug 5, 1:30 PM")
 * - Other years → "MMM D, YYYY h:mm A" (e.g., "Dec 15, 2025 1:30 PM")
 */
const formatSmartDateTime = (value: string): string => {
  const date = dayjs(value);
  if (!date.isValid()) return '-';
  if (date.isToday()) {
    return i18next.t('Today {{time}}', { ns: 'analytics', time: date.format('h:mm A') });
  }
  if (date.year() === dayjs().year()) {
    return date.format('MMM D, h:mm A');
  }
  return date.format('MMM D, YYYY h:mm A');
};

/**
 * Cell renderer for date/time values.
 * Accepts either a format string or an options object.
 *
 * @param options - A dayjs format string, or an options object with `smart` and/or `format` fields.
 * @returns A cell renderer function
 *
 * @example
 * // Default format (MMM D, YYYY h:mm A):
 * { cellRenderer: dateTimeCellRenderer() }
 *
 * // Custom format:
 * { cellRenderer: dateTimeCellRenderer('MMM D, YYYY') }
 *
 * // Smart formatting (Today h:mm A / MMM D, h:mm A / MMM D, YYYY h:mm A):
 * { cellRenderer: dateTimeCellRenderer({ smart: true }) }
 */
export const dateTimeCellRenderer =
  (options?: DateTimeCellRendererOptions) =>
  (value: string): string => {
    if (!value) return '-';

    if (typeof options === 'object' && options?.smart) {
      return formatSmartDateTime(value);
    }

    const format = typeof options === 'string' ? options : options?.format ?? 'MMM D, YYYY h:mm A';
    const date = dayjs(value);
    return date.isValid() ? date.format(format) : '-';
  };

/**
 * Cell renderer for plain text values.
 * Returns the value as-is, or '-' if falsy.
 *
 * @example
 * { cellRenderer: textCellRenderer }
 */
export const textCellRenderer = (value: string): string => value || '-';
